import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import './ContestDetail.css';

export default function ContestDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contest, setContest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userPix, setUserPix] = useState(0);
  const [canClaim, setCanClaim] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadContest();
    loadUserPix();
  }, [id]);

  const loadContest = async () => {
    if (!id) return;
    
    try {
      const docRef = doc(db, 'contests_rewards', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setContest({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (error) {
      console.error('Error loading contest:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserPix = async () => {
    if (!auth.currentUser) return;

    try {
      // Get user's PIX balance from completedTasks
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();
      
      // Calculate total PIX from approved submissions
      const submissionsRef = collection(db, 'submissions');
      const q = query(
        submissionsRef,
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'approved')
      );
      
      const snapshot = await getDocs(q);
      let totalPix = 0;
      snapshot.forEach((doc) => {
        const data = doc.data();
        const reward = data.rewardAmount || data.reward || 0;
        totalPix += typeof reward === 'number' ? reward : 0;
      });
      
      setUserPix(totalPix);
    } catch (error) {
      console.error('Error loading user PIX:', error);
    }
  };

  useEffect(() => {
    if (contest && userPix >= contest.pixRequired) {
      setCanClaim(true);
    } else {
      setCanClaim(false);
    }
  }, [contest, userPix]);

  const handleClaim = async () => {
    if (!contest || !canClaim) return;
    
    // TODO: Implement claim logic
    alert(`Claim functionality will be implemented. You have ${userPix} PIX and need ${contest.pixRequired} PIX.`);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading contest...</div>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="page-container">
        <div className="error">Contest not found</div>
        <button onClick={() => navigate('/dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="page-container">
      <button onClick={() => navigate('/dashboard')} className="back-button">
        ← Back to Dashboard
      </button>

      <div className="contest-detail-card">
        <div className="contest-header">
          <h1>{contest.title}</h1>
          <span className={`contest-type ${contest.type}`}>
            {contest.type === 'contest' ? 'Contest' : 'Discount'}
          </span>
        </div>

        {contest.partnerName && (
          <p className="partner-name">Partner: {contest.partnerName}</p>
        )}

        <div className="contest-description-full">
          {contest.description}
        </div>

        <div className="contest-requirements">
          <div className="requirement-item">
            <span className="requirement-label">PIX Required:</span>
            <span className="requirement-value">{contest.pixRequired}</span>
          </div>
          <div className="requirement-item">
            <span className="requirement-label">Your PIX:</span>
            <span className={`requirement-value ${canClaim ? 'sufficient' : 'insufficient'}`}>
              {userPix.toFixed(2)}
            </span>
          </div>
        </div>

        {canClaim ? (
          <button onClick={handleClaim} className="claim-button">
            {contest.type === 'contest' ? 'Enter Contest' : 'Redeem Discount'}
          </button>
        ) : (
          <div className="cannot-claim">
            <p>You need {contest.pixRequired - userPix} more PIX to claim this reward.</p>
          </div>
        )}

        {contest.discountCode && (
          <div className="discount-code">
            <p>Discount Code: <strong>{contest.discountCode}</strong></p>
          </div>
        )}

        {contest.externalUrl && (
          <a href={contest.externalUrl} target="_blank" rel="noopener noreferrer" className="external-link">
            Visit Partner Website →
          </a>
        )}
      </div>
    </div>
  );
}



